import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Menu,
  ListItemText,
  TableSortLabel
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { usePermissions } from '../hooks/usePermissions';
import ColumnFilter, { FilterConfig } from './ColumnFilter';

interface User {
  id: number;
  username: string;
  role: {
    id: number;
    name: string;
  };
  shop: {
    id: number;
    name: string;
    address: string;
  } | null;
  status: string;
  password?: string; // Optional field for editing purposes
}

interface Shop {
  id: number;
  name: string;
  address: string;
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [users, setUsers] = useState<User[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    roleName: 'Administrator',
    shopId: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('username');
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});

  // Hardcoded role options like in REFDashboard.tsx
  const roleOptions = [
    { value: 'Factory Inventory Adder', label: 'Factory Inventory Adder' },
    { value: 'Shop POS Operator and Daily Demand Sender', label: 'Shop POS Operator and Daily Demand Sender' },
    { value: 'Factory POS Operator', label: 'Factory POS Operator' },
    { value: 'Factory Inventory Dispatcher', label: 'Factory Inventory Dispatcher' },
    { value: 'Administrator', label: 'Administrator' },
    { value: 'Director', label: 'Director' },
    { value: 'Factory Raw Inventory Adder', label: 'Factory Raw Inventory Adder' },
    { value: 'Bag-Box Printer View', label: 'Bag-Box Printer View' }
  ];

  const columnFilterConfigs: FilterConfig[] = [
    { field: 'username', type: 'string', label: 'Username', placeholder: 'Filter by username' },
    { field: 'roleName', type: 'select', label: 'Role', options: roleOptions },
    { field: 'shopId', type: 'select', label: 'Shop', options: shops.map(shop => ({ value: shop.id.toString(), label: shop.name })) }
  ];

  useEffect(() => {
    if (token) {
      fetchUsers();
      fetchShops();
    }
  }, [token]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: token }
      });
      setUsers(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching users:', err);
      }
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shops`, {
        headers: { Authorization: token }
      });
      setShops(response.data);
    } catch (err: any) {
      console.error('Error fetching shops:', err);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim() || !newUser.roleName) {
      return;
    }

    try {
      setIsSubmitting(true);
      const userData = {
        username: newUser.username,
        password: newUser.password,
        roleName: newUser.roleName,
        ...(newUser.roleName === 'Shop POS Operator and Daily Demand Sender' && { shopName: shops.find(shop => shop.id === newUser.shopId)?.name || '' })
      };
      
      await axios.post(`${API_BASE_URL}/users`, userData, {
        headers: { Authorization: token }
      });
      
      setShowAddUserDialog(false);
      setNewUser({
        username: '',
        password: '',
        roleName: 'Administrator',
        shopId: 0
      });
      fetchUsers();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('You do not have permission to add users');
      } else {
        console.error('Failed to add user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    
    setIsSubmitting(true);
    try {
      const userData: any = {};
      
      // Only include password if it's not empty
      if (editingUser.password && editingUser.password.trim() !== '') {
        userData.password = editingUser.password;
      }
      
      // Include status
      if (editingUser.status) {
        userData.status = editingUser.status;
      }
      
      const response = await axios.put(
        `${API_BASE_URL}/users/${editingUser.id}`,
        userData,
        {
          headers: { Authorization: token }
        }
      );
      
      setUsers(prev => prev.map(user => 
        user.id === editingUser.id ? response.data : user
      ));
      setShowEditUserDialog(false);
      setEditingUser(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('You do not have permission to edit users');
      } else {
        console.error('Failed to update user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (action: string) => {
    handleActionsMenuClose();
    switch (action) {
      case 'add':
        setShowAddUserDialog(true);
        break;
    }
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const sortedUsers = [...users].filter(user => {
    return Object.entries(columnFilters).every(([field, filterValue]) => {
      if (!filterValue || filterValue === '') return true;
      
      switch (field) {
        case 'username':
          return user.username.toLowerCase().includes(filterValue.toLowerCase());
        case 'roleName':
          return user.role.name === filterValue;
        case 'shopId':
          return user.shop?.id.toString() === filterValue;
        default:
          return true;
      }
    });
  }).sort((a, b) => {
    let aValue: any;
    let bValue: any;
    
    switch (orderBy) {
      case 'username':
        aValue = a.username;
        bValue = b.username;
        break;
      case 'roleName':
        aValue = a.role.name;
        bValue = b.role.name;
        break;
      case 'shopId':
        aValue = a.shop?.name || '';
        bValue = b.shop?.name || '';
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        aValue = a[orderBy as keyof User];
        bValue = b[orderBy as keyof User];
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {hasPermission('add-user') && (
            <>
              <Button
                variant="outlined"
                onClick={handleActionsMenuOpen}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Actions
              </Button>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={handleActionsMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: '70vh',
                    width: '100%',
                    maxWidth: '280px'
                  }
                }}
              >
                <MenuItem onClick={() => handleActionSelect('add')}>
                  <ListItemText primary="Add User" />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, maxWidth: '600px' }}>
              {/* Search can be added here if needed */}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
              {hasPermission('add-user') && (
                <Button variant="contained" onClick={() => setShowAddUserDialog(true)} size="small">
                  Add User
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            fontSize: '0.875rem'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'username'}
                    direction={orderBy === 'username' ? order : 'asc'}
                    onClick={() => handleSort('username')}
                  >
                    Username
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[0]}
                    value={columnFilters.username}
                    onChange={(value) => handleColumnFilterChange('username', value)}
                    onClear={() => handleColumnFilterClear('username')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'roleName'}
                    direction={orderBy === 'roleName' ? order : 'asc'}
                    onClick={() => handleSort('roleName')}
                  >
                    Role
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[1]}
                    value={columnFilters.roleName}
                    onChange={(value) => handleColumnFilterChange('roleName', value)}
                    onClear={() => handleColumnFilterClear('roleName')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'shopId'}
                    direction={orderBy === 'shopId' ? order : 'asc'}
                    onClick={() => handleSort('shopId')}
                  >
                    Shop
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[2]}
                    value={columnFilters.shopId}
                    onChange={(value) => handleColumnFilterChange('shopId', value)}
                    onClear={() => handleColumnFilterClear('shopId')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingUsers ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.role.name}</TableCell>
                  <TableCell>{user.shop ? user.shop.name : 'N/A'}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      display: 'inline-block',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: user.status === 'Active' ? '#e8f5e8' : '#ffe8e8',
                      color: user.status === 'Active' ? '#2e7d32' : '#d32f2f',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {user.status}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {hasPermission('edit-user') && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingUser({ ...user, password: '' });
                          setShowEditUserDialog(true);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onClose={() => setShowAddUserDialog(false)}>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            fullWidth
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            disabled={isSubmitting}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            disabled={isSubmitting}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={newUser.roleName}
              onChange={(e) => setNewUser({ ...newUser, roleName: e.target.value })}
              label="Role"
              disabled={isSubmitting}
            >
              {roleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {newUser.roleName === 'Shop POS Operator and Daily Demand Sender' && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Shop</InputLabel>
              <Select
                value={newUser.shopId}
                onChange={(e) => setNewUser({ ...newUser, shopId: Number(e.target.value) })}
                label="Shop"
                disabled={isSubmitting}
              >
                {shops.map((shop) => (
                  <MenuItem key={shop.id} value={shop.id}>
                    {shop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddUserDialog(false)} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onClose={() => setShowEditUserDialog(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            fullWidth
            value={editingUser?.username || ''}
            disabled
            helperText="Username cannot be changed"
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={editingUser?.password || ''}
            onChange={(e) => setEditingUser({ ...editingUser!, password: e.target.value })}
            disabled={isSubmitting}
            helperText="Leave empty to keep current password"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={editingUser?.status || 'Active'}
              onChange={(e) => setEditingUser({ ...editingUser!, status: e.target.value })}
              label="Status"
              disabled={isSubmitting}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Blocked">Blocked</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditUserDialog(false)} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
